/* eslint-disable no-unused-vars */
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  GoogleAnalyticsConstants,
  GoogleAnalyticsDisplayedEvent,
  GoogleAnalyticsEventContextWrapper,
} from '@vvoyages/google-analytics';

import Button from '@/components/Button';
import { WizardStep } from '@/components/Wizard';
import { appUiIds } from '@/constants/appUiIds';
import useAppUiData from '@/hooks/useAppUiData';

import styles from './ClaimBookingStep.module.scss';

const MONTH_INDEX_START = 1;
const FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
};

type Sailor = {
  clientId: string;
  name: {
    first: string;
    last: string;
  };
  email: string;
  dob: string;
  sex: string;
  citizenship: string;
};

type ClaimBookingDetailsConfirmationProps = {
  currentUser: null | {
    email: string;
  };
  tabId: string;
  data: {
    bookingNumber: string;
    selectedDOB?: string;
    isManualEntry?: boolean;
    sailors: Sailor[];
    updateProfileEmail?: boolean;
  };
  onSubmit: Function;
  onCancel: () => void;
};

const ClaimBookingDetailsConfirmation: React.FC<ClaimBookingDetailsConfirmationProps> = ({
  currentUser,
  tabId,
  data,
  onSubmit,
  onCancel,
}) => {
  const appUiData = useAppUiData();
  const [matchedSailor] = data.sailors || [];
  const hasMatchedSailorEmail = !!matchedSailor.email;
  const shouldUpdateEmail = data.updateProfileEmail || !matchedSailor.email;
  const updateProfileEmail = hasMatchedSailorEmail && shouldUpdateEmail;
  const birthday = useMemo(() => {
    const [year, month, day] = (matchedSailor?.dob || data?.selectedDOB || '').split('-');
    const date = new Date(+year, +month - MONTH_INDEX_START, +day);
    return date.toLocaleDateString('en-US', FORMAT_OPTIONS);
  }, [matchedSailor.dob, data?.selectedDOB]);

  const handleConfirm = () => {
    const value = {
      bookingNumber: data.bookingNumber,
      updateProfileEmail,
      selectedDOB: data?.selectedDOB,
      user: {
        clientID: matchedSailor.clientId,
        lastName: matchedSailor.name?.last,
        email: matchedSailor.email ?? currentUser?.email,
        birthday: matchedSailor?.dob && matchedSailor?.dob?.trim() !== '' ? matchedSailor.dob : null,
      },
      claimConfirmed: true,
      isManualEntry: !!data.isManualEntry,
    };
    onSubmit(value);
  };

  return (
    <WizardStep tabId={tabId}>
      <GoogleAnalyticsEventContextWrapper category="VV-account-sign-in|check-your-booking-details">
        <GoogleAnalyticsDisplayedEvent eventAction={GoogleAnalyticsConstants.EVENT_ACTION.DISPLAYED} />
        <h2 className={styles.title}>{appUiData[appUiIds.claimBooking.verifyDetailsTitle]}</h2>
        <p className={styles.subtitle}>{appUiData[appUiIds.claimBooking.verifyDetailsSubtitle]}</p>
        <form className={styles.selectForm}>
          <div>
            <b className={styles.sailorDetailsHeading}>{appUiData[appUiIds.claimBooking.reservationDetails]}</b>
          </div>
          <div className={styles.sailorDetails}>
            <div className={styles.emailText}>
              Email: {updateProfileEmail ? matchedSailor.email : currentUser?.email}
            </div>
            <div>Firstname: {matchedSailor.name.first}</div>
            <div>Surname: {matchedSailor.name.last}</div>
            <div>Date of Birth: {birthday}</div>
            {matchedSailor?.sex && <div>Sex: {matchedSailor.sex}</div>}
            {matchedSailor?.citizenship && <div>Citizenship: {matchedSailor.citizenship}</div>}
          </div>
          <Button variant="primary" onClick={handleConfirm} className={styles.buttonClaimYourBooking}>
            {appUiData[appUiIds.claimBooking.claimYourBooking]}
          </Button>
          <Button onClick={onCancel} className={styles.cancelLink}>
            {appUiData[appUiIds.commonLabels.cancel]}
          </Button>
        </form>
      </GoogleAnalyticsEventContextWrapper>
    </WizardStep>
  );
};

export default ClaimBookingDetailsConfirmation;
